import {
  mapToUiOrder,
  UiOrder,
  ApiProductionOrderWithProduct,
} from '../mappers/productionOrderMapper';
import { BaseApiService } from './BaseApiService';
import { formatDate, formatTime } from '../utils/date';

export interface ProductionOrderScheduleEntry {
  id: number;
  production_order_id: number;
  portion_number: number;
  schedule_type: 'production' | 'pause';
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes: number;
  duration_seconds?: number;
  estimated_production_minutes?: number;
  actual_start_time?: string;
  actual_end_time?: string;
  notes?: string;
  production_line_id?: number;
  created_at: string;
  updated_at: string;
}

export interface ProductionOrder {
  production_order_id: number;
  product_id: number;
  quantity_cases: number;
  quantity_units: number;
  due_date?: string;
  priority_level: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  estimated_production_hours?: number; // Average based on 6 workers baseline
  notes?: string;
  created_by?: number;
  actual_start_time?: string; // Order-level actual start time
  actual_end_time?: string; // Order-level actual end time
  batch_number?: string;
  actual_cases_produced?: number;
  actual_units_produced?: number;
  production_issues?: string;
  bulk_used_notes?: string;
  bloom_stock_deduct_status?: 'pending' | 'completed' | null;
  is_paused?: boolean;
  pause_start_time?: string;
  total_pause_duration_seconds?: number;
  created_at: string;
  updated_at: string;
}

export interface ProductionOrderWithProduct extends ProductionOrder {
  product_name: string;
  product_code: string;
  units_per_case: number;
  packing_speed_units: number;
  packing_speed_time: number;
  packing_speed_unit: string;
  shelf_life_days?: number;
  product_production_line_id?: number;
  production_line_id?: number;
  production_line_name?: string;
  planned_production_date?: string | null; // From schedule entries
  planned_production_time?: string | null; // From schedule entries
  box_size?: string;
  container_name?: string;
  size?: string;
  containers_per_box?: number;
  label_requirements?: string;
}

/** Reads the current-pause-duration API payload (seconds). */
export function pause_duration_seconds_from_api(data: {
  current_pause_duration_seconds?: number;
} | null | undefined): number {
  return Number(data?.current_pause_duration_seconds) || 0;
}

export interface FloorBoardStaleRow {
  production_order_id: number;
  earliest_scheduled_date: string;
}

export interface ProductionOrderBulkRequirement {
  id: number;
  production_order_id: number;
  bulk_product_id: number;
  cases_needed: number;
  cases_used?: number;
  usage_notes?: string;
  bulk_product: {
    id: number;
    name: string;
    sku_number: string;
    vendor?: string;
  };
}

export interface CreateProductionOrderData {
  product_id: number;
  quantity_cases: number;
  quantity_units: number;
  due_date?: string;
  priority_level?: 'low' | 'normal' | 'high' | 'urgent';
  notes?: string;
  production_line_id?: number;
  planned_production_date?: string;
  planned_production_time?: string;
}

export interface UpdateProductionOrderData {
  product_id?: number;
  quantity_cases?: number;
  quantity_units?: number;
  due_date?: string;
  priority_level?: 'low' | 'normal' | 'high' | 'urgent';
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  production_line_id?: number;
  estimated_production_hours?: number;
  notes?: string;
  planned_production_date?: string | null;
  planned_production_time?: string | null;
}

export interface WeeklySchedule {
  id: number;
  day_of_week: number;
  worker_hours: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ScheduleOverride {
  id: number;
  override_date: string;
  worker_hours: number;
  reason?: string;
  created_at: string;
  updated_at: string;
}

export interface ProductionOrderFilters {
  status?: string;
  priority?: string;
  startDate?: string;
  endDate?: string;
  productId?: number;
  productionLineId?: number;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'created_at' | 'due_date' | 'priority_level';
  sortOrder?: 'ASC' | 'DESC';
}

export interface ProductionOrderPaginatedResponse {
  success: boolean;
  data: ProductionOrderWithProduct[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

class ProductionOrderService extends BaseApiService {
  // Production Orders CRUD
  async getAllProductionOrders(
    filters?: ProductionOrderFilters
  ): Promise<ProductionOrderWithProduct[]> {
    const response = await this.get<{ success: boolean; data: ProductionOrderWithProduct[] }>(
      '/production-orders',
      filters
    );
    return response.data || [];
  }

  async getProductionOrdersPaginated(
    filters?: ProductionOrderFilters
  ): Promise<ProductionOrderPaginatedResponse> {
    const response = await this.get<ProductionOrderPaginatedResponse>(
      '/production-orders',
      filters
    );
    return response;
  }

  async getCalendarView(startDate: string, endDate: string): Promise<UiOrder[]> {
    const response = await this.get<{ success: boolean; data: ApiProductionOrderWithProduct[] }>(
      `/production-orders/calendar/${startDate}/${endDate}`
    );
    const apiOrders = response.data || [];
    return apiOrders.map(mapToUiOrder);
  }

  /** Production floor dashboard — full list for a work date (not first page of generic list). */
  async getFloorBoard(params: {
    work_date: string;
    production_line_id?: number;
    show_completed?: boolean;
  }): Promise<{ orders: ProductionOrderWithProduct[]; stale_active: FloorBoardStaleRow[] }> {
    const query: Record<string, string | number> = {
      work_date: params.work_date,
    };
    if (params.production_line_id != null) {
      query.production_line_id = params.production_line_id;
    }
    if (params.show_completed) {
      query.show_completed = 'true';
    }
    const response = await this.get<{
      success: boolean;
      data: ProductionOrderWithProduct[];
      stale_active: FloorBoardStaleRow[];
    }>('/production-orders/floor-board', query);
    return {
      orders: response.data || [],
      stale_active: response.stale_active || [],
    };
  }

  async getProductionOrderById(id: number): Promise<ProductionOrderWithProduct | null> {
    const response = await this.get<{ success: boolean; data: ProductionOrderWithProduct }>(
      `/production-orders/${id}`
    );
    return response.data || null;
  }

  async createProductionOrder(data: CreateProductionOrderData): Promise<ProductionOrder> {
    const response = await this.post<{ success: boolean; data: ProductionOrder }>(
      '/production-orders',
      data
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to create production order');
    }
    return response.data;
  }

  async updateProductionOrder(
    id: number,
    data: UpdateProductionOrderData
  ): Promise<ProductionOrder> {
    const response = await this.put<{ success: boolean; data: ProductionOrder }>(
      `/production-orders/${id}`,
      data
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to update production order');
    }
    return response.data;
  }

  async updateCompletedProductionOrder(
    id: number,
    data: {
      actual_start_time?: string;
      actual_end_time?: string;
      actual_cases_produced?: number;
      actual_units_produced?: number;
    }
  ): Promise<ProductionOrder> {
    const response = await this.put<{ success: boolean; data: ProductionOrder }>(
      `/production-orders/${id}/completed`,
      data
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to update completed production order');
    }
    return response.data;
  }

  async updateProductionOrderStatus(
    id: number,
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled',
    reason?: string
  ): Promise<ProductionOrder> {
    const response = await this.put<{ success: boolean; data: ProductionOrder }>(
      `/production-orders/${id}/status`,
      { status, reason }
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to update production order status');
    }
    return response.data;
  }

  async deleteProductionOrder(id: number): Promise<void> {
    const response = await this.delete<{ success: boolean }>(`/production-orders/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete production order');
    }
  }

  async getBulkRequirements(id: number): Promise<ProductionOrderBulkRequirement[]> {
    const response = await this.get<{ success: boolean; data: ProductionOrderBulkRequirement[] }>(
      `/production-orders/${id}/bulk-requirements`
    );
    return response.data || [];
  }

  // Scheduling
  async getWeeklySchedule(): Promise<WeeklySchedule[]> {
    const response = await this.get<{ success: boolean; data: WeeklySchedule[] }>(
      '/scheduling/weekly'
    );
    return response.data || [];
  }

  async updateWeeklySchedule(
    dayOfWeek: number,
    workerHours: number,
    isActive: boolean = true
  ): Promise<WeeklySchedule> {
    const response = await this.put<{ success: boolean; data: WeeklySchedule }>(
      `/scheduling/weekly/${dayOfWeek}`,
      {
        worker_hours: workerHours,
        is_active: isActive,
      }
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to update weekly schedule');
    }
    return response.data;
  }

  async getScheduleOverrides(startDate?: string, endDate?: string): Promise<ScheduleOverride[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.get<{ success: boolean; data: ScheduleOverride[] }>(
      '/scheduling/overrides',
      params
    );
    return response.data || [];
  }

  async createScheduleOverride(
    overrideDate: string,
    workerHours: number,
    reason?: string
  ): Promise<ScheduleOverride> {
    const response = await this.post<{ success: boolean; data: ScheduleOverride }>(
      '/scheduling/overrides',
      {
        override_date: overrideDate,
        worker_hours: workerHours,
        reason,
      }
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to create schedule override');
    }
    return response.data;
  }

  async deleteScheduleOverride(date: string): Promise<void> {
    const response = await this.delete<{ success: boolean }>(`/scheduling/overrides/${date}`);
    if (!response.success) {
      throw new Error('Failed to delete schedule override');
    }
  }

  async getAvailableHours(date: string): Promise<number> {
    const response = await this.get<{
      success: boolean;
      data: { date: string; available_hours: number };
    }>(`/scheduling/available-hours/${date}`);
    return response.data?.available_hours || 0;
  }

  // Utility methods
  formatPriorityLevel(priority: string): string {
    const priorityMap: Record<string, string> = {
      low: 'Low',
      normal: 'Normal',
      high: 'High',
      urgent: 'Urgent',
    };
    return priorityMap[priority] || priority;
  }

  formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return statusMap[status] || status;
  }

  getPriorityColor(priority: string): string {
    const colorMap: Record<string, string> = {
      low: '#28a745', // Green
      normal: '#6c757d', // Gray
      high: '#fd7e14', // Orange
      urgent: '#dc3545', // Red
    };
    return colorMap[priority] || '#6c757d';
  }

  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      pending: '#ffc107', // Yellow
      in_progress: '#007bff', // Blue
      completed: '#28a745', // Green
      cancelled: '#6c757d', // Gray
    };
    return colorMap[status] || '#6c757d';
  }

  // Use centralized date utilities instead of duplicate methods
  formatDate(dateString: string): string {
    return formatDate(dateString);
  }

  formatTime(timeString: string): string {
    return formatTime(timeString);
  }

  getDayName(dayOfWeek: number): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || '';
  }

  calculateTotalUnits(quantityCases: number, quantityUnits: number, unitsPerCase: number): number {
    return quantityCases * unitsPerCase + quantityUnits;
  }

  formatQuantity(quantityCases: number, quantityUnits: number): string {
    const parts = [];
    if (quantityCases > 0) parts.push(`${quantityCases} case${quantityCases !== 1 ? 's' : ''}`);
    if (quantityUnits > 0) parts.push(`${quantityUnits} unit${quantityUnits !== 1 ? 's' : ''}`);
    return parts.join(' + ') || '0';
  }

  // Enhanced Production Workflow Methods
  async startProductionOrder(id: number, overrideCases?: number): Promise<ProductionOrder> {
    const response = await this.post<{ success: boolean; data: ProductionOrder }>(
      `/production-orders/${id}/start`,
      {
        override_cases: overrideCases,
      }
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to start production order');
    }
    return response.data;
  }

  async completeProductionOrder(
    id: number,
    completionData: {
      actualCasesProduced: number;
      actualUnitsProduced: number;
      productionIssues?: string;
      bulkUsedNotes?: string;
      bulkUsage?: Array<{ bulk_product_id: number; cases_used: number; notes?: string }>;
      createNewOrderForLeftover?: boolean;
      newOrderDueDate?: string;
    }
  ): Promise<{
    productionOrder: ProductionOrder;
    newOrderId?: number;
    bloom_stock_warnings?: string[];
    bloom_stock_deduct_pending?: boolean;
  }> {
    const response = await this.post<{
      success: boolean;
      data: {
        productionOrder: ProductionOrder;
        newOrderId?: number;
        bloom_stock_warnings?: string[];
        bloom_stock_deduct_pending?: boolean;
      };
    }>(`/production-orders/${id}/complete`, {
      actual_cases_produced: completionData.actualCasesProduced,
      actual_units_produced: completionData.actualUnitsProduced,
      production_issues: completionData.productionIssues,
      bulk_used_notes: completionData.bulkUsedNotes,
      bulk_usage: completionData.bulkUsage,
      create_new_order_for_leftover: completionData.createNewOrderForLeftover,
      new_order_due_date: completionData.newOrderDueDate,
    });
    if (!response.success || !response.data) {
      throw new Error('Failed to complete production order');
    }
    return response.data;
  }

  /** Split a completion response into the success line and a Bloom stock problem, if any. */
  complete_notice(
    production_order_id: number,
    result: {
      newOrderId?: number;
      bloom_stock_warnings?: string[];
      bloom_stock_deduct_pending?: boolean;
    }
  ): { completed_message: string; bloom_stock_message: string | null } {
    let completed_message = `Production order #${production_order_id} completed successfully`;
    if (result.newOrderId) {
      completed_message += `. New order #${result.newOrderId} created for leftover cases.`;
    }
    const warnings = (result.bloom_stock_warnings || []).filter(Boolean);
    if (result.bloom_stock_deduct_pending) {
      return {
        completed_message,
        bloom_stock_message: `Bloom stock was not reduced. ${warnings.join(' ')} Use Retry Bloom stock on this order.`,
      };
    }
    if (warnings.length > 0) {
      return {
        completed_message,
        bloom_stock_message: `Bloom stock was only partly reduced: ${warnings.join('; ')}`,
      };
    }
    return { completed_message, bloom_stock_message: null };
  }

  async getBulkUsage(id: number): Promise<any[]> {
    const response = await this.get<{ success: boolean; data: any[] }>(
      `/production-orders/${id}/bulk-usage`
    );
    return response.data || [];
  }

  // Pause/Continue Production Order Methods
  async pauseProductionOrder(id: number, pauseReason?: string): Promise<ProductionOrder> {
    const response = await this.post<{ success: boolean; data: ProductionOrder }>(
      `/production-orders/${id}/pause`,
      {
        pause_reason: pauseReason,
      }
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to pause production order');
    }
    return response.data;
  }

  async continueProductionOrder(id: number): Promise<ProductionOrder> {
    const response = await this.post<{ success: boolean; data: ProductionOrder }>(
      `/production-orders/${id}/continue`,
      {}
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to resume production order');
    }
    return response.data;
  }

  async getPauseHistory(id: number): Promise<any[]> {
    const response = await this.get<{ success: boolean; data: any[] }>(
      `/production-orders/${id}/pause-history`
    );
    return response.data || [];
  }

  async getCurrentPauseDuration(id: number): Promise<number> {
    const response = await this.get<{
      success: boolean;
      data: { current_pause_duration_seconds?: number };
    }>(`/production-orders/${id}/current-pause-duration`);
    return pause_duration_seconds_from_api(response.data);
  }

  // Schedule Entries
  async getScheduleEntries(orderId: number): Promise<ProductionOrderScheduleEntry[]> {
    const response = await this.get<{ success: boolean; data: ProductionOrderScheduleEntry[] }>(
      `/production-orders/${orderId}/schedule-entries`
    );
    return response.data || [];
  }

  async getScheduleEntriesForDate(
    date: string,
    productionLineId?: number
  ): Promise<ProductionOrderScheduleEntry[]> {
    const params: Record<string, string> = { date };
    if (productionLineId !== undefined) {
      params.production_line_id = productionLineId.toString();
    }
    const response = await this.get<{ success: boolean; data: ProductionOrderScheduleEntry[] }>(
      '/production-orders/schedule-entries',
      params
    );
    return response.data || [];
  }
}

export const productionOrderService = new ProductionOrderService();
